import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Group from 'components/utils/Group'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import React, { useMemo } from 'react'
import CellContent from './CellContent'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useOffers from 'hooks/Offers/useOffers'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { isLEHotel } from 'lib/offer/offerTypes'

interface Props {
  offer: App.HotelOffer
  filters: App.OfferListFilters
  packageId: string
  selectionId: string
  selections: Array<App.OfferComparisonSelection>
}

function getOfferInclusions(offer: App.AnyOffer, packageId?: string): Array<App.PackageInclusion> {
  if (!isLEHotel(offer)) {
    return []
  }

  const selectedPackage = packageId ? offer.packages.find(p => p.id === packageId) : undefined
  if (selectedPackage) {
    return selectedPackage.inclusions || []
  }

  if (offer.tileInclusions?.length) {
    return offer.tileInclusions
  }

  if (offer.lowestPricePackage?.inclusions?.length) {
    return offer.lowestPricePackage.inclusions
  }

  if (offer.lowestPricePackage?.roomType.amenities.length) {
    return offer.lowestPricePackage.roomType.amenities
  }

  const pkgWithInclusions = offer.packages.find((p) => p.inclusions?.length)
  return pkgWithInclusions?.inclusions || []
}

function isInclusionInAllOtherOffers(
  inclusion: App.PackageInclusion,
  currentSelectionId: string,
  selections: Array<App.OfferComparisonSelection>,
  allOffers: Array<App.AnyOffer>,
): boolean {
  const otherSelections = selections.filter(s => s.selectionId !== currentSelectionId)

  return otherSelections.every(selection => {
    const otherOffer = allOffers.find(o => o.id === selection.offerId)
    if (!otherOffer) return false

    const otherInclusions = getOfferInclusions(otherOffer, selection.params?.packageId)
    return otherInclusions.some(otherInclusion => otherInclusion.description === inclusion.description)
  })
}

function LEHotelExclusiveInclusionsCell({
  filters,
  offer,
  packageId,
  selectionId,
  selections,
}: Props) {
  const selectedPackage = offer.packages.find(p => p.id === packageId)!

  const { luxPlusInclusions } = useOfferInclusions(
    selectedPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const luxPlusSearchTileInclusions = useMemo<Array<App.PackageInclusion>>(() => {
    if (offer.luxPlusTileInclusions?.length) {
      return offer.luxPlusTileInclusions
    }
    return luxPlusInclusions
  }, [luxPlusInclusions, offer.luxPlusTileInclusions])

  const [allOffers] = useOffers(selections.map(s => s.offerId))

  const [commonInclusions, exclusiveInclusions] = useMemo(() => {
    const allInclusions = selectedPackage.inclusions || []

    const common = allInclusions.filter(inclusion => isInclusionInAllOtherOffers(inclusion, selectionId, selections, allOffers))
    const exclusive = allInclusions.filter(inclusion => !isInclusionInAllOtherOffers(inclusion, selectionId, selections, allOffers))

    return [common, exclusive]
  }, [selectedPackage, selectionId, selections, allOffers])

  return <CellContent>
    <VerticalSpacer gap={8}>
      <BodyText variant="medium" weight="bold">Select packages only:</BodyText>
      <OfferInclusionsList type="lux-plus" inclusions={luxPlusSearchTileInclusions} />
      <Group direction="vertical" gap={4}>
        {exclusiveInclusions.map((inclusion) => <OfferInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          isHighlighted={false}
        />)}
        {commonInclusions.map((inclusion) => <OfferInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          colour="neutral-four"
          isHighlighted={false}
        />)}
      </Group>
    </VerticalSpacer>
  </CellContent>
}

export default LEHotelExclusiveInclusionsCell
